// src/tabs/logements/VilleLogementEditor.jsx
//
// Champ « Ville » du logement, rendu dans la carte « Adresse » de l'onglet
// 📖 Livret > 📋 Instructions — juste sous « Adresse complète (voyageur) ».
//
// ── POURQUOI CE CHAMP EXISTE ────────────────────────────────────────────
// La variable {ville} (message d'acceptation d'une demande Direct :
// « Logement : {logement_name} — {ville} ») se résout depuis la config KV du
// logement : `public_address.ville`, puis `city` en repli
// (lib/direct-vars-catalog.js `_villeOfLog`, lib/template-variables.js
// `case "ville"`). Or AUCUN écran de l'admin n'écrivait ces champs : les
// seules valeurs en base (lc1, lc3) sont des reliquats d'un ancien import
// Beds24 (lib/beds24-mapping-handlers.js construit bien `public_address`,
// mais la modale d'import ne l'applique plus). Le propriétaire ne pouvait
// donc pas remplir la ville. Ce champ est cet écran manquant.
//
// ⚠️ `livret_config.city` (renseignée pour les 11 logements) n'alimente PAS
// {ville} : aucun résolveur ne la lit. On n'y touche pas — la source de
// vérité de la variable est la config KV, c'est là qu'on écrit.
//
// ── OÙ ÇA ÉCRIT ─────────────────────────────────────────────────────────
// EXACTEMENT le path que lisent les résolveurs : `log.public_address.ville`.
// Aucun second chemin d'écriture : on passe par les helpers déjà utilisés par
// les champs voisins de la carte Adresse — `onApplyPatchAndSave`
// (LogementsPanel.applyPatchAndSaveNow, save AWAITÉ, `{ ok, error }` vérifié,
// erreur AFFICHÉE) et, en repli, `onUpdate` (brouillon + 💾 Enregistrer).
//
// ── VIDE = CLÉ ABSENTE, PAS CHAÎNE VIDE (motif PR #1117) ────────────────
// Les textes Direct masquent la ligne par `{#if ville}`. Une chaîne vide
// serait une valeur « présente » côté blob : on envoie donc `undefined`, que
// `JSON.stringify` (la sérialisation réelle de saveCfg, src/lib/cfg-api.js)
// SUPPRIME du blob persisté. Même motif que les règles Animaux / Fumeur.
// Vider la ville alors que `public_address` porte encore une autre donnée
// (ex. `code_postal` sur lc1/lc3) ne retire QUE `ville` ; si l'objet devient
// vide de sens, c'est `public_address` entier qui disparaît. Vider retire aussi
// `city` si elle porte une valeur (SECONDE source de la même variable, cf.
// `buildVillePatch`) — sinon « vidé » serait mensonger.
//
// ── LIMITE CONNUE (pré-existante, commune à tous les champs de la carte) ──
// `applyPatchAndSaveNow` persiste TOUT le brouillon en cours (sémantique
// existante), et le 💾 global lit ce brouillon depuis un état React : cliquer
// 💾 pile pendant l'enregistrement du champ reste la course déjà présente sur
// les règles Animaux/Fumeur et le détecteur de barème. Non aggravée ici ; le
// champ se contente d'attendre son save et d'afficher l'échec.
//
// ── AUCUNE ÉCRITURE AU MONTAGE ──────────────────────────────────────────
// Le champ est un brouillon LOCAL. On n'écrit qu'au blur (ou Entrée) ET
// seulement si la valeur a réellement changé : afficher la fiche de lc1, y
// cliquer, en sortir sans rien taper n'enregistre RIEN — la ville héritée de
// l'ancien import ne peut pas être écrasée par un simple passage.
//
// ── SOUS-LOCATION ───────────────────────────────────────────────────────
// Aucune cascade ici : le composant lit et écrit UNIQUEMENT le logement
// affiché (lc1/lc2/lc3 sont autonomes, ADR-197). La cascade enfant→parent
// existe au RENDU des variables Direct (resolveVilleWithParentFallback) et
// n'est pas touchée. `ville` n'est volontairement PAS ajoutée à la whitelist
// « Copier vers les chambres » : une copie de masse poserait une ville sur
// une chambre qui n'en a pas.

import { useState, useEffect, useRef } from "react"
import { Field, Row, inpStyle } from "./atoms"
import { FicheAlert } from "./_ficheUi"
import { DS } from "../../lib/ui"

// cfg → champ. Même lecture que les résolveurs : `public_address.ville`
// (objet strict), repli `city`. Toute structure inattendue ⇒ "".
export function villeOfLog(log) {
  if (!log || typeof log !== "object") return ""
  const pa = log.public_address
  if (pa && typeof pa === "object" && !Array.isArray(pa)) {
    const v = pa.ville
    if (v != null && String(v).trim() !== "") return String(v)
  }
  if (log.city != null && String(log.city).trim() !== "") return String(log.city)
  return ""
}

// Champ → patch cfg (spread shallow côté applyPatchAndSaveNow / updateLog).
// Saisie vide ⇒ `ville: undefined` (clé supprimée à la sérialisation), et
// `public_address: undefined` s'il ne reste plus rien de porteur de sens.
// Les autres clés de `public_address` (quartier, code_postal) sont PRÉSERVÉES.
//
// ⚠️ VIDER retire AUSSI `city` si elle porte une valeur : `city` est la SECONDE
// source de la MÊME variable (les 3 résolveurs font `public_address.ville ||
// city`). La laisser derrière rendrait « champ vidé » mensonger — {ville}
// continuerait de s'afficher et `{#if ville}` resterait vrai. En prod `city` est
// null sur les 11 logements ; le patch reste donc byte-identique dans les faits.
// Une saisie NON vide, elle, ne touche pas `city` (public_address.ville est
// prioritaire partout où elle est lue).
export function buildVillePatch(log, raw) {
  const v = raw == null ? "" : String(raw).trim()
  const pa = log && typeof log === "object" ? log.public_address : null
  // `public_address` mal typée (chaîne, tableau) : on ne tente pas de la
  // préserver, on repart d'un objet propre — le résolveur l'ignore de toute
  // façon (`typeof pa === "object"` strict).
  const base = pa && typeof pa === "object" && !Array.isArray(pa) ? pa : null

  if (v !== "") return { public_address: { ...(base || {}), ville: v } }

  const cityPorteuse =
    log && typeof log === "object" && log.city != null && String(log.city).trim() !== ""
  const efface = cityPorteuse ? { city: undefined } : {}

  if (!base) return { ...efface, public_address: undefined }
  const rest = { ...base, ville: undefined }
  const gardeAutreChose = Object.keys(rest).some(
    (k) => k !== "ville" && rest[k] != null && String(rest[k]).trim() !== "",
  )
  return { ...efface, public_address: gardeAutreChose ? rest : undefined }
}

export default function VilleLogementEditor({ log, isChambre = false, onUpdate, onApplyPatchAndSave }) {
  const stored = villeOfLog(log)

  // Brouillon LOCAL : la frappe n'écrit rien. Re-synchronisé au CHANGEMENT DE
  // LOGEMENT uniquement (sélectionner un autre logement doit réafficher SA
  // ville) — jamais pendant la frappe, sinon une saisie en cours sauterait.
  const [draft, setDraft] = useState(stored)
  const logIdRef = useRef(log?.id)
  useEffect(() => {
    if (logIdRef.current !== log?.id) {
      logIdRef.current = log?.id
      setDraft(stored)
    }
  }, [log?.id, stored])

  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)
  // Valeur en cours d'enregistrement — garde d'IDEMPOTENCE : « Entrée » puis le
  // blur qu'elle provoque (navigateur réel) ne déclenchent qu'UN seul save.
  const pendingRef = useRef(null)

  // Pose le patch dans le BROUILLON cfg (helper des champs voisins). Le patch
  // peut porter 2 clés (`public_address` + `city` à l'effacement) : on les pose
  // toutes, sinon l'effacement serait partiel.
  function applyToDraft(patch) {
    for (const [field, value] of Object.entries(patch)) onUpdate?.(field, value)
  }

  async function commit() {
    const next = String(draft ?? "").trim()
    // ⚠️ GARDE ANTI-ÉCRASEMENT : rien n'a changé ⇒ AUCUNE écriture. Un
    // focus/blur sur la fiche de lc1 ou lc3 ne réenregistre pas leur ville.
    if (next === stored) { setError(null); return }
    if (pendingRef.current === next) return

    const patch = buildVillePatch(log, next)
    setError(null)
    setSaved(false)

    // Repli (montages sans persistance immédiate) : brouillon + 💾 global.
    if (typeof onApplyPatchAndSave !== "function") {
      applyToDraft(patch)
      return
    }

    setSaving(true)
    pendingRef.current = next
    try {
      const r = await onApplyPatchAndSave(patch)
      if (!r || r.ok !== true) {
        setError((r && r.error) || "Enregistrement échoué")
        // La saisie n'est PAS perdue : elle reste dans le champ ET on la pose
        // dans le brouillon cfg pour que le 💾 Enregistrer global puisse la
        // reprendre (le repo a un historique d'écritures perdues en silence).
        applyToDraft(patch)
        return
      }
      setDraft(next) // normalise l'affichage sur la valeur réellement persistée
      setSaved(true)
    } catch (e) {
      console.error("[VilleLogementEditor] save", e)
      setError(e.message || "Enregistrement échoué")
      applyToDraft(patch)
    } finally {
      setSaving(false)
      pendingRef.current = null
    }
  }

  return (
    <div
      style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #eef1f5" }}
      data-testid="ville-logement-editor"
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: DS.navy, marginBottom: 4 }}>
        🏙️ Ville
      </div>
      {/* Texte d'aide = état ACTUEL (règle permanente Dimitri) : on décrit ce
          que la valeur fait aujourd'hui, sans promettre d'autre usage. */}
      <div style={{ fontSize: 11, color: "#888", lineHeight: 1.5, marginBottom: 10 }}>
        Utilisée par la variable <strong>{"{ville}"}</strong> des messages du
        parcours Direct (message d'acceptation : « Logement : … — Ville »).
        Enregistrée à la sortie du champ. <strong>Vide</strong> : aucune ville
        n'est affichée, la mention disparaît du message.
        {isChambre && (
          <>
            {" "}Chaque chambre a sa propre ville ; vide, le message d'acceptation
            reprend celle du logement parent (s'il en a une).
          </>
        )}
      </div>

      <Row>
        <Field label="Ville">
          {/* <input> natif (et non l'atome <Inp>) : il faut onBlur + Entrée,
              que l'atome partagé n'expose pas — on réutilise SON style exporté
              plutôt que d'élargir un atome utilisé par toutes les fiches. */}
          <input
            type="text"
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setSaved(false); setError(null) }}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return
              // Entrée = « je valide » : on enregistre ET on rend le focus.
              // Le blur ainsi provoqué ne double PAS le save (pendingRef).
              e.preventDefault()
              e.currentTarget.blur()
              commit()
            }}
            maxLength={120}
            placeholder="ex: Toulouse"
            disabled={saving}
            aria-label="Ville du logement"
            style={{
              ...inpStyle,
              background: saving ? "#eef2f7" : "#f8fafd",
              color: saving ? "#888" : "#444",
            }}
          />
        </Field>
      </Row>

      {saving && (
        <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 6 }}>
          Enregistrement…
        </div>
      )}
      {saved && !error && (
        <FicheAlert tone="success" style={{ marginTop: 6 }} testId="ville-logement-ok">
          ✓ Ville enregistrée.
        </FicheAlert>
      )}
      {error && (
        <FicheAlert tone="danger" style={{ marginTop: 6 }} testId="ville-logement-error">
          {error} — la ville n'a PAS été enregistrée. Réessayez, ou utilisez
          💾 Enregistrer (la saisie est conservée dans le brouillon).
        </FicheAlert>
      )}
    </div>
  )
}
